import { useEffect, useState } from "react"
import { supabase } from "../../lib/supabase"
import { useAuth } from "../../contexts/AuthContext"
import { openExternal } from "../../lib/openExternal"
import { useNavigate } from "react-router-dom"
import { useLanguage } from "../../contexts/LanguageContext"
import useIsMobile from "../../lib/useIsMobile"
import toast from "react-hot-toast"
import DarajaPayment from "../shared/DarajaPayment"
import { GOServiceIcon, ServicesIcon, DiscoverIcon, MarketplaceIcon, LocationIcon, ShieldIcon, ShareIcon, PaymentsIcon, DeleteIcon, VehicleIcon, ChatIcon, DownloadIcon, DeliveryIcon, WarningIcon } from "../../lib/cccIcons"


import { downloadInvoice, downloadBookingsCSV } from "../../lib/invoice"
import ChatWindow from "../shared/ChatWindow"

const SC = { pending:"#e6821e", confirmed:"#378add", "in-progress":"#8b5cf6", completed:"#1d9e75", cancelled:"#e24b4a" }
const SB = { pending:"#fff8f0", confirmed:"#eff6ff", "in-progress":"#faf5ff", completed:"#f0fdf4", cancelled:"#fff5f5" }

export default function CustomerBookings() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const { t } = useLanguage()
  const isMobile = useIsMobile()
  const [bookings, setBookings] = useState([])
  const [openClaimBookingIds, setOpenClaimBookingIds] = useState(new Set())
  const [filter, setFilter] = useState("all")
  const [loading, setLoading] = useState(true)
  const [expanded, setExpanded] = useState(null)
  const [rebooking, setRebooking] = useState(null)
  const [serviceFeeBooking, setServiceFeeBooking] = useState(null)
  const [search, setSearch] = useState("")
  const [invoiceLoading, setInvoiceLoading] = useState(null)
  const [driverInfo, setDriverInfo] = useState({})
  const [providerPhones, setProviderPhones] = useState({})
  const [chatBooking, setChatBooking] = useState(null)

  async function loadProviderPhone(providerId, bookingId) {
    if (!providerId || providerPhones[bookingId]) return
    const [{ data: sens }, { data: prof }] = await Promise.all([
      supabase.from("profile_sensitive").select("phone").eq("id", providerId).single(),
      supabase.from("profiles").select("first_name,last_name,business_name").eq("id", providerId).single(),
    ])
    setProviderPhones(prev=>({...prev,[bookingId]:{ phone:sens?.phone, name:prof?.business_name||(prof?.first_name+" "+prof?.last_name) }}))
  }

  async function loadDriverInfo(driverId, bookingId) {
    if (!driverId || driverInfo[bookingId]) return
    const [{ data: driver }, { data: status }] = await Promise.all([
      supabase.from("profiles").select("first_name,last_name").eq("id",driverId).single(),
      supabase.from("driver_status").select("is_online,current_booking_id").eq("driver_id",driverId).maybeSingle(),
    ])
    if (driver) setDriverInfo(prev=>({...prev,[bookingId]:{...driver,...status}}))
  }

  async function downloadBookingInvoice(booking) {
    setInvoiceLoading(booking.id)
    try {
      const [{ data: provider }, { data: customer }, { data: mechanic }] = await Promise.all([
        supabase.from("profiles").select("*").eq("id", booking.provider_id).single(),
        supabase.from("profiles").select("*").eq("id", booking.customer_id).single(),
        booking.assigned_mechanic_id ? supabase.from("mechanics").select("*").eq("id", booking.assigned_mechanic_id).single() : Promise.resolve({ data:null }),
      ])
      downloadInvoice(booking, provider, customer, mechanic, null)
      toast.success("Invoice downloaded")
    } catch(err) {
      toast.error("Could not generate invoice")
    } finally {
      setInvoiceLoading(null)
    }
  }

  const [rebookForm, setRebookForm] = useState({ date:"", time:"" })

  useEffect(() => {
    if (!user) return
    load()
    const sub = supabase.channel("customer-bookings-"+user.id)
      .on("postgres_changes", { event:"UPDATE", schema:"public", table:"bookings", filter:`customer_id=eq.${user.id}` }, () => load())
      .subscribe()
    return () => supabase.removeChannel(sub)
  }, [user])

  async function load() {
    const { data } = await supabase.from("bookings").select("*,profiles!bookings_driver_id_fkey(first_name,last_name)").eq("customer_id", user.id).eq("hidden_from_customer", false).order("created_at",{ascending:false})
    setBookings(data||[])
    const { data: claims } = await supabase.from("service_claims").select("booking_id").eq("customer_id", user.id).in("status", ["pending","under_review"])
    setOpenClaimBookingIds(new Set((claims||[]).map(c=>c.booking_id).filter(Boolean)))
    setLoading(false)
  }

  async function approveParts(booking) {
    const { error } = await supabase.from("bookings").update({ parts_approved:true }).eq("id",booking.id).eq("customer_id",user.id)
    if (error) return toast.error(error.message)
    await supabase.from("notifications").insert({
      user_id: booking.provider_id,
      title: "Parts approved by customer ✅",
      message: `Customer has approved the parts for booking ${booking.booking_number}. Total: KES ${Number(booking.updated_total||booking.total_amount).toLocaleString()}`,
      type: "success",
    })
    toast.success("Parts approved — provider notified")
    load()
  }

  async function confirmPayment(booking) {
    if(!confirm("Confirm you are satisfied with the service and release payment to the provider?")) return
    try {
      const resp = await fetch("https://gcnefnqtjxtqbhynyoxe.supabase.co/functions/v1/release-payment", {
        method:"POST",
        headers:{"Content-Type":"application/json","Authorization":"Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImdjbmVmbnF0anh0cWJoeW55b3hlIiwicm9sZSI6ImFub24iLCJpYXQiOjE3Nzk2MDg0MzIsImV4cCI6MjA5NTE4NDQzMn0.Ybyce3psBj2I-hdoF95H5UAklr6hsgQi-mciI9uMIgc"},
        body:JSON.stringify({ booking_id:booking.id, confirmed_by:"customer" })
      })
      const data = await resp.json()
      if(data.error) throw new Error(data.error)
      if (data.partial) {
        toast(data.note || "Part of the payment was released; the rest is on hold.", { icon: "⏳" })
      } else {
        toast.success("Payment released! Thank you for confirming. ✅")
      }
      load()
    } catch(err) { toast.error(err.message) }
  }
  async function hideBooking(id) {
    if (!confirm("Remove this booking from your list?")) return
    const { error } = await supabase.from("bookings").update({ hidden_from_customer:true }).eq("id",id).eq("customer_id",user.id)
    if (error) return toast.error(error.message)
    toast.success("Booking removed from your list")
    load()
  }

  async function cancelBooking(id) {
    if (!confirm("Are you sure you want to cancel this booking?")) return
    const { error } = await supabase.from("bookings").update({ status:"cancelled" }).eq("id",id).eq("customer_id",user.id)
    if (error) return toast.error(error.message)
    // Notify provider
    const cancelled = bookings.find(b=>b.id===id)
    if (cancelled?.provider_id) {
      await supabase.from("notifications").insert({ user_id: cancelled.provider_id, title: "Booking cancelled", message: "Customer cancelled " + cancelled.service_name + " #" + (cancelled.booking_number||"") + " on " + cancelled.booking_date, type: "error" })
    }
    toast.success("Booking cancelled")
    const remove = confirm("Remove this booking from your list? It will still be kept in our records.")
    if (remove) {
      await supabase.from("bookings").update({ hidden_from_customer:true }).eq("id",id).eq("customer_id",user.id)
    }
    load()
  }

  async function rebook(e) {
    e.preventDefault()
    const b = bookings.find(b=>b.id===rebooking)
    if (!b) return
    const { error } = await supabase.from("bookings").insert({
      customer_id: user.id,
      provider_id: b.provider_id,
      service_id: b.service_id,
      service_name: b.service_name,
      booking_date: rebookForm.date,
      booking_time: rebookForm.time,
      total_amount: b.total_amount,
      payment_method: b.payment_method,
      status: "pending",
      payment_status: "pending"
    })
    if (error) return toast.error(error.message)
    await supabase.from("notifications").insert({ user_id: b.provider_id, title: "New booking!", message: b.service_name + " rebooked for " + rebookForm.date + " at " + rebookForm.time, type: "success" })
    await supabase.from("notifications").insert({ user_id: user.id, title: "Booking confirmed ✅", message: "Your "+b.service_name+" has been rebooked for "+rebookForm.date+" at "+rebookForm.time, type: "success" })
    toast.success("Booking created!")
    setRebooking(null)
    load()
  }

  const filtered = bookings.filter(b => {
    const matchFilter = filter==="all" || b.status===filter
    const matchSearch = !search || (b.service_name+b.booking_number+(b.notes||"")).toLowerCase().includes(search.toLowerCase())
    return matchFilter && matchSearch
  })
  const inp = { width:"100%", background:"#ffffff", border:"1px solid #e5e5e5", borderRadius:8, padding:"10px 12px", color:"#000000", fontSize:13, outline:"none", fontFamily:"'DM Sans',sans-serif" }

  return (
    <div>
      {/* Stats bar */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:8, marginBottom:"1rem" }}>
        {[
          { label:"Total", value:bookings.length, color:"#000" },
          { label:"Active", value:bookings.filter(b=>["pending","confirmed","in-progress"].includes(b.status)).length, color:"#378add" },
          { label:"Completed", value:bookings.filter(b=>b.status==="completed").length, color:"#1d9e75" },
          { label:"Cancelled", value:bookings.filter(b=>b.status==="cancelled").length, color:"#e24b4a" },
        ].map(s=>(
          <div key={s.label} style={{ background:"#f8f8f8", borderRadius:10, padding:"0.6rem", textAlign:"center", border:"1px solid #eee" }}>
            <div style={{ fontFamily:"Syne", fontSize:15, fontWeight:800, color:s.color }}>{s.value}</div>
            <div style={{ fontSize:10, color:"#888", marginTop:2 }}>{s.label}</div>
          </div>
        ))}
      </div>
      {/* Search */}
      <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search by service name or booking number..."
        style={{ width:"100%", background:"#f8f8f8", border:"1px solid #eee", borderRadius:8, padding:"9px 12px", fontSize:13, outline:"none", marginBottom:"0.75rem", boxSizing:"border-box" }}/>
      <div style={{ display:"flex", gap:6, marginBottom:"1rem", flexWrap:"wrap" }}>
        {["all","pending","confirmed","in-progress","completed","cancelled"].map(s=>(
          <button key={s} onClick={()=>setFilter(s)}
            style={{ padding:isMobile?"5px 10px":"6px 14px", borderRadius:6, border:"none", fontSize:isMobile?11:12, cursor:"pointer", background:filter===s?"#e6821e":"#f0f0f0", color:filter===s?"#fff":"#555", fontFamily:"'DM Sans',sans-serif" }}>
            {s==="all"?"All":s.charAt(0).toUpperCase()+s.slice(1)}
          </button>
        ))}
      </div>
      {bookings.length>0&&(
        <button onClick={()=>downloadBookingsCSV(bookings.map(b=>({...b,provider_name:b.providers?.business_name||b.providers?.first_name||"",vehicle_plate:b.vehicles?.license_plate||""})))}
          style={{ marginBottom:8, background:"#f8f8f8", border:"1px solid #ddd", borderRadius:8, color:"#555", fontSize:11, padding:"6px 14px", cursor:"pointer", display:"block", marginLeft:"auto" }}>
          <DownloadIcon size={14} color="#378add" /> Export CSV
        </button>
      )}

      {loading&&<div style={{ color:"#777777", fontSize:13 }}>{t("loading")}</div>}
      {!loading&&filtered.length===0&&<div style={{ color:"#888888", fontSize:13, textAlign:"center", padding:"2rem" }}>{t("noBookingsFound")}</div>}

      {filtered.map(b=>(
        <div key={b.id} style={{ background:"#ffffff", border:"1px solid #eee", borderLeft:`4px solid ${SC[b.status]||"#eee"}`, borderRadius:10, padding:isMobile?"0.75rem":"1rem", marginBottom:8 }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:8 }}>
            <div style={{ flex:1, minWidth:0, marginRight:8 }}>
              <div style={{ fontSize:isMobile?13:14, fontWeight:500, color:"#000000", marginBottom:2, display:"flex", alignItems:"center", gap:6 }}>
                <span style={{ fontSize:16 }}>
                  {b.service_category==="go_service"?<GOServiceIcon size={16} color="#e24b4a"/>:b.service_category==="shop_premium"?<ServicesIcon size={16} color="#e6821e"/>:b.service_category==="car_wash"||b.service_category==="basic_wash"||b.service_category==="standard_wash"||b.service_category==="premium_detail"?<DiscoverIcon size={16} color="#1d9e75"/>:<MarketplaceIcon size={16} color="#378add"/>}
                </span>
                {b.service_name}
              </div>
              <div style={{ fontSize:11, color:"#777777" }}>{b.booking_date} · {b.booking_time?.slice(0,5)}</div>
              {b.booking_number&&<div style={{ fontSize:10, color:"#888888", marginTop:2 }}>#{b.booking_number}</div>}
            </div>
            <div style={{ textAlign:"right", flexShrink:0 }}>
              <span style={{ fontSize:10, fontWeight:500, padding:"2px 8px", borderRadius:20, background:SB[b.status]||"#f5f5f5", color:SC[b.status]||"#888", border:`1px solid ${SC[b.status]||"#888"}40`, display:"inline-block" }}>{b.status}</span>
              <div style={{ fontFamily:"Syne", fontSize:isMobile?13:14, fontWeight:700, color:"#e6821e", marginTop:4 }}>KES {Number(b.total_amount).toLocaleString()}</div>
            </div>
          </div>

          <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
            {["confirmed","in-progress"].includes(b.status)&&(
              <button onClick={()=>navigate("/dashboard/tracking")}
                style={{ background:"#eff6ff", border:"1px solid #378add40", borderRadius:7, color:"#378add", fontSize:11, padding:"5px 10px", cursor:"pointer" }}>
                <LocationIcon size={13} color="#378add" /> Track
              </button>
            )}
            {["pending","confirmed"].includes(b.status)&&(
              <button onClick={()=>cancelBooking(b.id)}
                style={{ background:"none", border:"1px solid #e24b4a40", borderRadius:7, color:"#e24b4a", fontSize:11, padding:"5px 10px", cursor:"pointer" }}>
                {t("cancelBooking")}
              </button>
            )}
            {b.payment_held&&!b.payment_released&&["in-progress","completed"].includes(b.status)&&(
              <button onClick={()=>confirmPayment(b)}
                style={{ background:"#1d9e75", border:"none", borderRadius:7, color:"#fff", fontSize:11, fontWeight:700, padding:"5px 12px", cursor:"pointer" }}>
                ✅ Confirm & Release Payment
              </button>
            )}
            {b.payment_held&&!b.payment_released&&["in-progress","completed"].includes(b.status)&&(
              <div style={{ fontSize:10, color:"#888", padding:"4px 8px", background:"#f8f8f8", borderRadius:6 }}>💰 Payment held · auto-releases in 24hrs</div>
            )}
            {b.status==="completed"&&(
              <>
                <button onClick={()=>navigate("/dashboard/reviews?booking="+b.id)}
                  style={{ background:"#fff8f0", border:"1px solid #e6821e40", borderRadius:7, color:"#e6821e", fontSize:11, padding:"5px 10px", cursor:"pointer" }}>
                  ⭐ Review
                </button>
                <button onClick={()=>navigate(`/dashboard/claims?booking=${b.id}`)}
                  style={{ background:"#fff5f5", border:"1px solid #e24b4a30", borderRadius:7, color:"#e24b4a", fontSize:11, padding:"5px 10px", cursor:"pointer" }}>
                  <ShieldIcon size={13} color="#1d9e75" /> Service guarantee
                </button>
                <button onClick={()=>downloadBookingInvoice(b)} disabled={invoiceLoading===b.id}
                  style={{ background:"#f0fdf4", border:"1px solid #1d9e7540", borderRadius:7, color:"#1d9e75", fontSize:11, padding:"5px 10px", cursor:"pointer" }}>
                  {invoiceLoading===b.id?"...":"⬇ Invoice"}
                </button>
                <button onClick={()=>{
                  const msg = encodeURIComponent(`Just got my ${b.service_name} done via Car Care Connect! Book your service too: https://carcareconnect.care`)
                  openExternal(`https://wa.me/?text=${msg}`)
                }}
                  style={{ background:"#eff6ff", border:"1px solid #378add40", borderRadius:7, color:"#378add", fontSize:11, padding:"5px 10px", cursor:"pointer" }}>
                  <ShareIcon size={13} color="#64748B" /> Share
                </button>
                <button onClick={()=>{ setRebooking(b.id); setRebookForm({ date:"", time:"" }) }}
                style={{ background:"#fff8f0", border:"1px solid #e6821e40", borderRadius:7, color:"#e6821e", fontSize:11, padding:"5px 10px", cursor:"pointer" }}>
                {t("rebookService")}
                </button>
              </>
              )}
              {b.service_category==="go_service"&&b.status==="completed"&&!b.service_fee_paid&&(
                <button onClick={()=>setServiceFeeBooking(b)}
                  style={{ background:"#e6821e", border:"none", borderRadius:7, color:"#fff", fontSize:11, fontWeight:700, padding:"5px 10px", cursor:"pointer" }}>
                  <PaymentsIcon size={13} color="#e6821e" /> Pay Service Fee
                </button>
              )}
              {(b.status==="completed"||b.status==="cancelled")&&(
                <button onClick={()=>hideBooking(b.id)}
                  style={{ background:"none", border:"1px solid #55540", borderRadius:7, color:"#777777", fontSize:11, padding:"5px 10px", cursor:"pointer" }}>
                  <DeleteIcon size={13} color="#e24b4a" /> Hide
                </button>
              )}
            <button onClick={()=>setExpanded(expanded===b.id?null:b.id)}
              style={{ background:"none", border:"1px solid #dddddd", borderRadius:7, color:"#666", fontSize:11, padding:"5px 10px", cursor:"pointer" }}>
              {expanded===b.id?t("less"):t("details")}
            </button>
          </div>

          {b.is_concierge&&b.driver_id&&(
            <div style={{ marginTop:10, background:"#eff6ff", border:"1px solid #378add30", borderRadius:10, padding:"0.9rem" }}>
              <div style={{ fontFamily:"Syne", fontSize:12, fontWeight:700, color:"#378add", marginBottom:8, display:"flex", alignItems:"center", gap:4 }}><VehicleIcon size={14} color="#378add" /> Concierge delivery</div>
              {/* Driver info */}
              {b.driver_id&&(
                <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:10 }}
                  ref={el=>{ if(el&&!driverInfo[b.id]) loadDriverInfo(b.driver_id,b.id) }}>
                  <div style={{ width:32, height:32, borderRadius:"50%", background:"#f0fdf4", border:"1px solid #1d9e7540", display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"Syne", fontSize:12, fontWeight:800, color:"#1d9e75", flexShrink:0 }}>
                    {driverInfo[b.id]?.first_name?.[0]}{driverInfo[b.id]?.last_name?.[0]}
                  </div>
                  <div>
                    <div style={{ fontSize:12, color:"#000000" }}>Driver: {driverInfo[b.id]?.first_name} {driverInfo[b.id]?.last_name}</div>
                    <div style={{ fontSize:10, color:"#777777" }}>Concierge driver assigned</div>
                  </div>
                </div>
              )}
              {/* Concierge progress */}
              <div style={{ display:"flex", gap:4, flexWrap:"wrap" }}>
                {[
                  { k:"accepted", l:"Assigned" },
                  { k:"pickup", l:"At pickup" },
                  { k:"in_transit", l:"To provider" },
                  { k:"at_provider", l:"At provider" },
                  { k:"return_transit", l:"Returning" },
                  { k:"dropoff", l:"At dropoff" },
                  { k:"completed", l:"Done" },
                ].map((step,i)=>{
                  const steps = ["accepted","pickup","in_transit","at_provider","return_transit","dropoff","completed"]
                  const currentIdx = steps.indexOf(b.concierge_status)
                  const isDone = i<=currentIdx
                  return (
                    <div key={step.k} style={{ fontSize:9, padding:"2px 7px", borderRadius:6, background:isDone?"#f0fdf4":"#ffffff", color:isDone?"#1d9e75":"#555555", border:`1px solid ${isDone?"#1d9e7530":"#f5f5f5"}` }}>
                      {isDone?"✓ ":""}{step.l}
                    </div>
                  )
                })}
              </div>
              {/* Transport allowance */}
              {b.transport_allowance>0&&(
                <div style={{ fontSize:11, color:"#777777", marginTop:8 }}>
                  <><DeliveryIcon size={13} color="#378add"/> Transport allowance: KES {Number(b.transport_allowance).toLocaleString()} included</>
                </div>
              )}
            </div>
          )}

          {b.parts_details?.length>0&&!b.parts_approved&&(
            <div style={{ marginTop:10, background:"#eff6ff", border:"1px solid #378add40", borderRadius:10, padding:"0.9rem" }}>
              <div style={{ fontFamily:"Syne", fontSize:13, fontWeight:700, color:"#378add", marginBottom:8, display:"flex", alignItems:"center", gap:4 }}><ServicesIcon size={14} color="#378add" /> Parts added by provider</div>
              {b.parts_details.map((p,i)=>(
                <div key={i} style={{ display:"flex", justifyContent:"space-between", fontSize:12, padding:"4px 0", borderBottom:"1px solid #eeeeee" }}>
                  <span style={{ color:"#555555" }}>{p.name} × {p.quantity}</span>
                  <span style={{ color:"#000000" }}>KES {p.total?.toLocaleString()}</span>
                </div>
              ))}
              <div style={{ display:"flex", justifyContent:"space-between", fontSize:13, fontWeight:700, color:"#e6821e", marginTop:8 }}>
                <span>New total</span><span>KES {Number(b.updated_total||b.total_amount).toLocaleString()}</span>
              </div>
              <div style={{ display:"flex", gap:8, marginTop:10 }}>
                <button onClick={()=>approveParts(b)}
                  style={{ background:"#1d9e75", border:"none", borderRadius:8, color:"#fff", fontFamily:"Syne,sans-serif", fontSize:12, fontWeight:700, padding:"8px 16px", cursor:"pointer" }}>
                  ✓ Approve parts
                </button>
                <button onClick={()=>cancelBooking(b.id)}
                  style={{ background:"none", border:"1px solid #e24b4a40", borderRadius:8, color:"#e24b4a", fontSize:12, padding:"8px 14px", cursor:"pointer" }}>
                  Decline & cancel
                </button>
              </div>
            </div>
          )}

          {expanded===b.id&&(
            <div style={{ marginBottom:10 }}>
              <button onClick={()=>setChatBooking(chatBooking===b.id?null:b.id)}
                style={{ background:"#eff6ff", border:"1px solid #378add40", borderRadius:7, color:"#378add", fontSize:11, padding:"5px 12px", cursor:"pointer", width:"100%" }}>
                <><ChatIcon size={13} color="#8b5cf6" /> {chatBooking===b.id?"Close chat":"Message provider"}</>
              </button>
              {chatBooking===b.id&&(
                <div style={{ height:320, marginTop:8 }}>
                  <ChatWindow
                    bookingId={b.id}
                    otherUserId={b.provider_id}
                    otherUserName={providerPhones[b.id]?.name||"Provider"}
                    onClose={()=>setChatBooking(null)}
                  />
                </div>
              )}
              {b.driver_id&&(
                <button onClick={()=>setChatBooking(chatBooking===b.id+"driver"?null:b.id+"driver")}
                  style={{ background:"#f0fdf4", border:"1px solid #1d9e7540", borderRadius:7, color:"#1d9e75", fontSize:11, padding:"5px 12px", cursor:"pointer", width:"100%", marginTop:6 }}>
                  <><ChatIcon size={13} color="#8b5cf6" /> {chatBooking===b.id+"driver"?"Close chat":"Message driver"}</>
                </button>
              )}
              {chatBooking===b.id+"driver"&&b.driver_id&&(
                <div style={{ height:320, marginTop:8 }}>
                  <ChatWindow
                    bookingId={b.id}
                    otherUserId={b.driver_id}
                    otherUserName="Driver"
                    onClose={()=>setChatBooking(null)}
                  />
                </div>
              )}
            </div>
          )}
          {expanded===b.id&&(
            <div style={{ marginTop:10, paddingTop:10, borderTop:"1px solid #eeeeee" }}>
              <div style={{ display:"grid", gridTemplateColumns:isMobile?"1fr 1fr":"repeat(3,1fr)", gap:8 }}>
                {[
                  { l:t("paymentStatus"), v:b.payment_status },
                  { l:"Payment method", v:b.payment_method },
                  { l:b.is_concierge?"Concierge":"Service type", v:b.is_concierge?"Yes":"Standard" },
                ].map(f=>(
                  <div key={f.l}>
                    <div style={{ fontSize:10, color:"#777777", textTransform:"uppercase", marginBottom:2 }}>{f.l}</div>
                    <div style={{ fontSize:12, color:"#000000" }}>{f.v||"—"}</div>
                  </div>
                ))}
              </div>
              {b.notes&&<div style={{ fontSize:11, color:"#666", fontStyle:"italic", marginTop:8 }}>Note: &quot;{b.notes}&quot;</div>}
            </div>
          )}

          {rebooking===b.id&&(
            <div style={{ marginTop:10, background:"#ffffff", borderRadius:8, padding:"1rem", border:"1px solid #e5e5e5" }}>
              <div style={{ fontFamily:"Syne", fontSize:13, fontWeight:700, marginBottom:10, color:"#000000" }}>{t("rebookService")}</div>
              <form onSubmit={rebook}>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:10 }}>
                  <div>
                    <label style={{ fontSize:11, color:"#666", display:"block", marginBottom:4 }}>Date</label>
                    <input type="date" value={rebookForm.date} onChange={e=>setRebookForm(f=>({...f,date:e.target.value}))} required min={new Date().toISOString().split("T")[0]} style={inp}/>
                  </div>
                  <div>
                    <label style={{ fontSize:11, color:"#666", display:"block", marginBottom:4 }}>Time</label>
                    <select value={rebookForm.time} onChange={e=>setRebookForm(f=>({...f,time:e.target.value}))} required style={inp}>
                      <option value="">Select time</option>
                      {["07:00","08:00","09:00","10:00","11:00","12:00","13:00","14:00","15:00","16:00","17:00","18:00"].map(t2=><option key={t2} value={t2}>{t2}</option>)}
                    </select>
                  </div>
                </div>
                <div style={{ display:"flex", gap:8 }}>
                  <button type="submit" style={{ background:"#e6821e", border:"none", borderRadius:7, color:"#fff", fontFamily:"Syne,sans-serif", fontSize:12, fontWeight:700, padding:"8px 16px", cursor:"pointer" }}>{t("confirmBooking")}</button>
                  <button type="button" onClick={()=>setRebooking(null)} style={{ background:"none", border:"1px solid #dddddd", borderRadius:7, color:"#555555", fontSize:12, padding:"8px 16px", cursor:"pointer" }}>{t("cancel")}</button>
                </div>
              </form>
            </div>
          )}
        </div>
      ))}
    {serviceFeeBooking&&(
      <DarajaPayment
        amount={Number(serviceFeeBooking.total_amount||0)}
        bookingId={serviceFeeBooking.id}
        providerId={serviceFeeBooking.provider_id}
        description={`Service fee - ${serviceFeeBooking.service_name}`}
        onSuccess={async()=>{ await supabase.from("bookings").update({service_fee_paid:true,service_fee_paid_at:new Date().toISOString()}).eq("id",serviceFeeBooking.id); setServiceFeeBooking(null); load(); toast.success("Service fee paid! Thank you.") }}
        onClose={()=>setServiceFeeBooking(null)}
      />
    )}
    </div>
  )
}








































